import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { toast } from 'sonner';
import { ClipboardCheck, CheckCircle2, AlertTriangle, Clock, MinusCircle, Users, Calendar, Loader2, Search, Award, ChevronDown } from 'lucide-react';
import BatchDetailPanel from './BatchDetailPanel';
import DprDayDetail from './DprDayDetail';
import BatchWhatsappButton from './BatchWhatsappButton';
import SyncFromCrmButton from './SyncFromCrmButton';
import { fmtDate } from './periodUtils';

const STATUS = {
  green: { label: 'Satisfactory', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  amber: { label: 'Issue Found', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertTriangle },
  red: { label: 'Pending', cls: 'bg-rose-100 text-rose-700 border-rose-200', icon: Clock },
  grey: { label: 'Not Applicable', cls: 'bg-slate-100 text-slate-500 border-slate-200', icon: MinusCircle },
};
const yesterdayStr = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };
const pad2 = (n) => String(n).padStart(2, '0');
const lastDayOfMonth = (y, m) => new Date(y, m, 0).getDate(); // m 1-based

const BATCH_TYPE_OPTS = [
  { value: 'all', label: 'All batch types' },
  { value: 'weekday', label: 'Weekday (Mon–Thu)' },
  { value: 'weekend', label: 'Weekend (Sat–Sun)' },
];

// Client-side mirror of backend _expected_sessions (weekday 1.5h Mon–Thu, weekend 2h Sat/Sun).
function expectedSessions(start, end, isWeekend) {
  const per = isWeekend ? 2 : 1.5;
  if (!start || !end) return { classes: 0, hours: 0, per };
  const d0 = new Date(start + 'T00:00:00'); const d1 = new Date(end + 'T00:00:00');
  if (isNaN(d0) || isNaN(d1) || d1 < d0) return { classes: 0, hours: 0, per };
  let cnt = 0; const cur = new Date(d0);
  while (cur <= d1) {
    const wd = cur.getDay(); // Sun=0 … Sat=6
    if (isWeekend ? (wd === 6 || wd === 0) : (wd >= 1 && wd <= 4)) cnt += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return { classes: cnt, hours: Math.round(cnt * per * 10) / 10, per };
}

const RadioRow = ({ q, value, onChange }) => (
  <div className="py-2 border-b border-slate-100 last:border-0" data-testid={`audit-q-${q.key}`}>
    <p className="text-[13px] text-slate-700 mb-1.5">{q.label}</p>
    <div className="flex flex-wrap gap-1.5">
      {q.options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(q.key, o)}
          data-testid={`audit-q-${q.key}-${o.replace(/\s/g, '-').toLowerCase()}`}
          className={`px-3 py-1 rounded-full text-[12px] font-medium border transition-colors ${value === o ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'}`}>{o}</button>
      ))}
    </div>
  </div>
);

const IssueBlock = ({ schema, issue, setIssue, testidPrefix = 'audit' }) => (
  <div className="p-3 rounded-lg bg-rose-50 border border-rose-100 space-y-2" data-testid={`${testidPrefix}-issue-block`}>
    <div>
      <Label className="text-[11px]">Issue Type(s) <span className="text-slate-400 font-normal">— select all that apply</span></Label>
      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`${testidPrefix}-issue-types`}>
        {(schema?.issue_types || []).map((t) => {
          const active = (issue.types || []).includes(t);
          return (
            <button key={t} type="button"
              onClick={() => setIssue((p) => ({ ...p, types: active ? p.types.filter((x) => x !== t) : [...(p.types || []), t] }))}
              data-testid={`${testidPrefix}-issue-type-${t.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${active ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-slate-600 border-slate-200 hover:border-rose-300'}`}>{t}</button>
          );
        })}
      </div>
    </div>
    <div>
      <Label className="text-[11px]">Severity</Label>
      <Select value={issue.severity} onValueChange={(v) => setIssue((p) => ({ ...p, severity: v }))}>
        <SelectTrigger className="h-9 mt-1" data-testid={`${testidPrefix}-issue-severity`}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>{(schema?.issue_severities || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-[11px]">Issue Description</Label>
      <Textarea value={issue.description} onChange={(e) => setIssue((p) => ({ ...p, description: e.target.value }))} rows={2} className="mt-1 text-[13px]" data-testid={`${testidPrefix}-issue-desc`} />
    </div>
  </div>
);

// Collapsible batch-detail section used inside both modals
const DetailToggle = ({ batchId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-3 py-2 text-[12px] font-semibold text-slate-600" data-testid="toggle-batch-detail">
        <span>Syllabus & student attendance</span><ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="p-3 pt-0">{<BatchDetailPanel batchId={batchId} />}</div>}
    </div>
  );
};

const KpiCard = ({ label, value, color, testid }) => (
  <Card className="p-3" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
    <p className={`text-2xl font-bold mt-0.5 ${color || 'text-slate-800'}`}>{value}</p>
  </Card>
);

// Groups rows by trainer_name; each trainer is a collapsible header revealing their batches.
function GroupedBatchTable({ rows, cols, onAudit, actionLabel, testid }) {
  const [open, setOpen] = useState({});
  const groups = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((r) => { if (!m.has(r.trainer_name)) m.set(r.trainer_name, []); m.get(r.trainer_name).push(r); });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);
  return (
    <Card className="p-0 overflow-hidden" data-testid={testid}>
      <div className="divide-y divide-slate-100">
        {groups.map(([trainer, list]) => {
          const c = { green: 0, amber: 0, red: 0, grey: 0 };
          list.forEach((r) => { c[r.status_color] = (c[r.status_color] || 0) + 1; });
          const isOpen = open[trainer];
          return (
            <div key={trainer} data-testid={`trainer-group-${trainer}`}>
              <button type="button" onClick={() => setOpen((p) => ({ ...p, [trainer]: !p[trainer] }))}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-violet-50/50 text-left" data-testid={`trainer-toggle-${trainer}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                  <Users className="w-4 h-4 text-violet-600 shrink-0" />
                  <span className="font-semibold text-slate-800 text-[13px] truncate">{trainer}</span>
                  <span className="text-[11px] text-slate-400">· {list.length} batch{list.length !== 1 ? 'es' : ''}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] shrink-0">
                  {c.green > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-0">{c.green} ok</Badge>}
                  {c.amber > 0 && <Badge className="bg-amber-100 text-amber-800 border-0">{c.amber} issue</Badge>}
                  {c.red > 0 && <Badge className="bg-rose-100 text-rose-700 border-0">{c.red} pending</Badge>}
                  {c.grey > 0 && <Badge className="bg-slate-100 text-slate-500 border-0">{c.grey} n/a</Badge>}
                </div>
              </button>
              {isOpen && (
                <div className="overflow-x-auto bg-slate-50/40">
                  <table className="w-full text-[12px]">
                    <thead className="bg-slate-100/70"><tr className="text-left text-slate-500">
                      {cols.map((col) => <th key={col.label} className="px-2.5 py-1.5 font-medium whitespace-nowrap">{col.label}</th>)}
                      <th className="px-2.5 py-1.5 font-medium">Status</th><th className="px-2.5 py-1.5 font-medium text-right">Action</th>
                    </tr></thead>
                    <tbody>
                      {list.map((r) => {
                        const st = STATUS[r.status_color] || STATUS.red; const StIcon = st.icon;
                        return (
                          <tr key={`${r.batch_id}-${r.date || ''}`} className="border-t border-slate-100" data-testid={`audit-batch-${r.batch_id}`}>
                            {cols.map((col) => <td key={col.label} className="px-2.5 py-1.5 whitespace-nowrap">{col.render(r)}</td>)}
                            <td className="px-2.5 py-1.5"><Badge className={`text-[9px] border ${st.cls}`}><StIcon className="w-3 h-3 mr-1" />{st.label}</Badge></td>
                            <td className="px-2.5 py-1.5 text-right"><Button size="sm" onClick={() => onAudit(r)} data-testid={`audit-open-${r.batch_id}`} className={r.audit_id ? 'bg-slate-700 hover:bg-slate-800 h-7 text-[11px]' : 'bg-violet-600 hover:bg-violet-700 h-7 text-[11px]'}>{r.audit_id ? 'Edit' : actionLabel}</Button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
        {groups.length === 0 && <div className="p-12 text-center text-slate-400" data-testid="audit-empty">No batches found.</div>}
      </div>
    </Card>
  );
}

export default function AuditFormPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState('daily');
  const [schema, setSchema] = useState(null);
  const [finalCount, setFinalCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => { api.get('/training/audit/schema').then((r) => setSchema(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-5" data-testid="audit-form-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><ClipboardCheck className="w-6 h-6 text-violet-600" />Trainer Audit</h1>
          <p className="text-sm text-slate-500 mt-0.5">Daily session audits against the DPR, plus a final audit when a batch completes.</p>
        </div>
        <SyncFromCrmButton onDone={() => setRefreshKey((k) => k + 1)} />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="audit-form-tabs">
          <TabsTrigger value="daily" data-testid="tab-daily-audit"><ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />Daily Audit</TabsTrigger>
          <TabsTrigger value="final" data-testid="tab-final-audit"><Award className="w-3.5 h-3.5 mr-1.5" />Final Batch Audit{finalCount > 0 && <Badge className="ml-1.5 bg-rose-500 text-white border-0 text-[10px] px-1.5">{finalCount}</Badge>}</TabsTrigger>
        </TabsList>
        <TabsContent value="daily" className="mt-4"><DailyAudit schema={schema} isAdmin={isAdmin} refreshKey={refreshKey} /></TabsContent>
        <TabsContent value="final" className="mt-4"><FinalAudit schema={schema} isAdmin={isAdmin} onCount={setFinalCount} refreshKey={refreshKey} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────── Daily Audit ───────────────────────────
function DailyAudit({ schema, isAdmin, refreshKey }) {
  const [periodType, setPeriodType] = useState('day');
  const [day, setDay] = useState(yesterdayStr());
  const [month, setMonth] = useState(yesterdayStr().slice(0, 7));
  const [fromDate, setFromDate] = useState(yesterdayStr());
  const [toDate, setToDate] = useState(yesterdayStr());
  const [trainerId, setTrainerId] = useState('all');
  const [managerId, setManagerId] = useState('all');
  const [statusF, setStatusF] = useState('all');
  const [batchType, setBatchType] = useState('all');
  const [batchSearch, setBatchSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, row: null });
  const [issueFound, setIssueFound] = useState('No');
  const [issue, setIssue] = useState({ types: [], severity: '', description: '' });
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (periodType === 'day') { params.mode = 'daily'; params.date = day; }
      else if (periodType === 'month') { const [y, m] = month.split('-').map(Number); params.mode = 'custom'; params.from_date = `${y}-${pad2(m)}-01`; params.to_date = `${y}-${pad2(m)}-${pad2(lastDayOfMonth(y, m))}`; }
      else { params.mode = 'custom'; params.from_date = fromDate; params.to_date = toDate; }
      if (trainerId !== 'all') params.trainer_id = trainerId;
      if (managerId !== 'all') params.manager_user_id = managerId;
      if (statusF !== 'all') params.status = statusF;
      if (batchType !== 'all') params.batch_type = batchType;
      const r = await api.get('/training/audit/batches', { params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load batches'); }
    finally { setLoading(false); }
  }, [periodType, day, month, fromDate, toDate, trainerId, managerId, statusF, batchType, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const list = data?.rows || [];
    if (!batchSearch.trim()) return list;
    const q = batchSearch.trim().toLowerCase();
    return list.filter((r) => `${r.batch_number} ${r.batch_name} ${r.trainer_name}`.toLowerCase().includes(q));
  }, [data, batchSearch]);

  const openAudit = (row) => {
    const t = row.issue?.types || (row.issue?.type ? [row.issue.type] : []);
    setIssueFound(t.length ? 'Yes' : 'No');
    setIssue({ types: t, severity: row.issue?.severity || '', description: row.issue?.description || '' });
    setRemarks(row.manager_remarks || '');
    setModal({ open: true, row });
  };

  const submit = async () => {
    const row = modal.row;
    if (issueFound === 'Yes' && (!issue.types.length || !issue.severity || !issue.description.trim())) {
      toast.error('Please select at least one issue type, a severity and a description.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/training/audit/submit', { date: row.date, batch_id: row.batch_id, issue: issueFound === 'Yes' ? issue : {}, manager_remarks: remarks });
      toast.success('Audit saved');
      setModal({ open: false, row: null });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save audit'); }
    finally { setSaving(false); }
  };

  const counts = useMemo(() => { const c = { green: 0, amber: 0, red: 0, grey: 0 }; (rows || []).forEach((r) => { c[r.status_color] = (c[r.status_color] || 0) + 1; }); return c; }, [rows]);
  const dcols = [
    { label: 'Batch No', render: (r) => <span className="font-bold text-violet-600">#{r.batch_number}</span> },
    { label: 'Batch Name', render: (r) => r.batch_name },
    { label: 'Conducted', render: (r) => <Badge className={`text-[9px] border-0 ${r.dpr_conducted === 'Yes' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{r.dpr_conducted}</Badge> },
    { label: 'Timing', render: (r) => r.timing || '—' },
    { label: 'Strength', render: (r) => r.strength },
    { label: 'Present', render: (r) => <span className="text-emerald-600 font-semibold">{r.present}</span> },
    { label: 'Absent', render: (r) => <span className={r.absent > 0 ? 'text-rose-600 font-semibold' : 'text-slate-300'}>{r.absent}</span> },
    { label: 'Mode', render: (r) => r.mode || '—' },
    { label: 'Reason', render: (r) => <span className="max-w-[140px] truncate inline-block align-bottom text-slate-500" title={r.dpr_reason || ''}>{r.dpr_reason || '—'}</span> },
    { label: 'Comments', render: (r) => <span className="max-w-[140px] truncate inline-block align-bottom text-slate-500" title={r.dpr_comments || ''}>{r.dpr_comments || '—'}</span> },
  ];

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-[11px] text-slate-500">Period</Label>
            <Select value={periodType} onValueChange={setPeriodType}><SelectTrigger className="h-9 mt-1 w-36" data-testid="audit-period-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Single day</SelectItem>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent></Select></div>
          {periodType === 'day' && <div><Label className="text-[11px] text-slate-500">Date</Label><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-9 mt-1 w-40" data-testid="audit-date" /></div>}
          {periodType === 'month' && <div><Label className="text-[11px] text-slate-500">Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 mt-1 w-40" data-testid="audit-month" /></div>}
          {periodType === 'custom' && <>
            <div><Label className="text-[11px] text-slate-500">From</Label><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 mt-1 w-40" data-testid="audit-from" /></div>
            <div><Label className="text-[11px] text-slate-500">To</Label><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 mt-1 w-40" data-testid="audit-to" /></div>
          </>}
          <div><Label className="text-[11px] text-slate-500">Trainer</Label>
            <Select value={trainerId} onValueChange={setTrainerId}><SelectTrigger className="h-9 mt-1 w-44" data-testid="audit-trainer-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All trainers</SelectItem>{(data?.trainers || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
          {isAdmin && <div><Label className="text-[11px] text-slate-500">Training Manager</Label>
            <Select value={managerId} onValueChange={setManagerId}><SelectTrigger className="h-9 mt-1 w-44" data-testid="audit-manager-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All managers</SelectItem>{(data?.managers || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}</SelectContent></Select></div>}
          <div><Label className="text-[11px] text-slate-500">Status</Label>
            <Select value={statusF} onValueChange={setStatusF}><SelectTrigger className="h-9 mt-1 w-40" data-testid="audit-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="red">Pending</SelectItem><SelectItem value="green">Satisfactory</SelectItem><SelectItem value="amber">Issue Found</SelectItem><SelectItem value="grey">Not Applicable</SelectItem></SelectContent></Select></div>
          <div><Label className="text-[11px] text-slate-500">Batch Type</Label>
            <Select value={batchType} onValueChange={setBatchType}><SelectTrigger className="h-9 mt-1 w-44" data-testid="audit-batchtype-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{BATCH_TYPE_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>
          <div className="flex-1 min-w-[160px]"><Label className="text-[11px] text-slate-500">Search</Label>
            <div className="relative mt-1"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" /><Input value={batchSearch} onChange={(e) => setBatchSearch(e.target.value)} placeholder="Batch no, name…" className="h-9 pl-8" data-testid="audit-batch-search" /></div></div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard testid="daily-kpi-total" label="Total Batches" value={rows.length} />
        <KpiCard testid="daily-kpi-done" label="Audited" value={counts.green + counts.amber} color="text-emerald-600" />
        <KpiCard testid="daily-kpi-pending" label="Pending" value={counts.red} color="text-rose-600" />
        <KpiCard testid="daily-kpi-issues" label="Issues" value={counts.amber} color="text-amber-600" />
        <KpiCard testid="daily-kpi-completion" label="Completion" value={`${rows.length ? Math.round((counts.green + counts.amber) / rows.length * 100) : 0}%`} color="text-violet-600" />
      </div>

      {loading ? <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading…</div>
        : <GroupedBatchTable rows={rows} cols={dcols} onAudit={openAudit} actionLabel="Audit" testid="daily-audit-table" />}

      <Dialog open={modal.open} onOpenChange={(o) => !o && setModal({ open: false, row: null })}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="audit-modal">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle className="text-base">Audit — Batch {modal.row?.batch_number} · {modal.row?.trainer_name}</DialogTitle>
                <p className="text-[11px] text-slate-500">{fmtDate(modal.row?.date)} · Batch: {fmtDate(modal.row?.start_date)} → {fmtDate(modal.row?.end_date)} · DPR: {modal.row?.dpr_conducted === 'Yes' ? 'Conducted' : 'Not conducted'} · {modal.row?.present}/{modal.row?.strength} present · {modal.row?.mode}</p>
              </div>
              {modal.row && <BatchWhatsappButton link={modal.row.whatsapp_link} source={modal.row.whatsapp_source} className="shrink-0 mt-0.5" />}
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-1 space-y-4">
            {/* DPR data (trainer-reported, shown as-is) */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
              {[['Conducted', modal.row?.dpr_conducted], ['Timing', modal.row?.timing || '—'], ['Strength', modal.row?.strength], ['Present', modal.row?.present], ['Absent', modal.row?.absent], ['Mode', modal.row?.mode || '—']].map(([k, v]) => (
                <div key={k} className="bg-slate-50 rounded-md py-1.5"><p className="text-[9px] uppercase tracking-wide text-slate-400">{k}</p><p className="text-[12px] font-semibold text-slate-700 truncate px-1">{v}</p></div>
              ))}
            </div>
            {(modal.row?.dpr_reason || modal.row?.dpr_comments) && <p className="text-[11px] text-slate-500">{modal.row?.dpr_reason && <span className="text-rose-600">Reason: {modal.row.dpr_reason}. </span>}{modal.row?.dpr_comments}</p>}
            {modal.row && <DetailToggle batchId={modal.row.batch_id} />}
            {modal.row && <DprDayDetail batchId={modal.row.batch_id} date={modal.row.date} />}
            {/* Issue */}
            <div>
              <Label className="text-[12px] font-semibold text-slate-700">Was there any issue observed in this batch?</Label>
              <div className="flex gap-1.5 mt-1.5" data-testid="audit-issue-found">
                {['No', 'Yes'].map((o) => (
                  <button key={o} type="button" onClick={() => setIssueFound(o)} data-testid={`audit-issue-found-${o.toLowerCase()}`}
                    className={`px-4 py-1 rounded-full text-[12px] font-medium border transition-colors ${issueFound === o ? (o === 'Yes' ? 'bg-rose-600 text-white border-rose-600' : 'bg-emerald-600 text-white border-emerald-600') : 'bg-white text-slate-600 border-slate-200'}`}>{o}</button>
                ))}
              </div>
            </div>
            {issueFound === 'Yes' && <IssueBlock schema={schema} issue={issue} setIssue={setIssue} />}
            <div><Label className="text-[11px]">Manager Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className="mt-1 text-[13px]" data-testid="audit-remarks" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal({ open: false, row: null })} className="h-9">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="h-9 bg-violet-600 hover:bg-violet-700" data-testid="audit-submit-btn">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Submit Audit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────── Final Batch Audit ───────────────────────────
function FinalAudit({ schema, isAdmin, onCount, refreshKey }) {
  const [trainerId, setTrainerId] = useState('all');
  const [managerId, setManagerId] = useState('all');
  const [statusF, setStatusF] = useState('all');
  const [batchType, setBatchType] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, row: null });
  const [answers, setAnswers] = useState({});
  const [issue, setIssue] = useState({ types: [], severity: '', description: '' });
  const [remarks, setRemarks] = useState('');
  const [rating, setRating] = useState('');
  const [actualStart, setActualStart] = useState('');
  const [actualEnd, setActualEnd] = useState('');
  const [saving, setSaving] = useState(false);
  const final = schema?.final || {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (trainerId !== 'all') params.trainer_id = trainerId;
      if (managerId !== 'all') params.manager_user_id = managerId;
      if (statusF !== 'all') params.status = statusF;
      if (batchType !== 'all') params.batch_type = batchType;
      const r = await api.get('/training/audit/final-batches', { params });
      setData(r.data);
      onCount?.(r.data.counts?.pending || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [trainerId, managerId, statusF, batchType, onCount, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const list = data?.rows || [];
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter((r) => `${r.batch_number} ${r.batch_name} ${r.trainer_name}`.toLowerCase().includes(q));
  }, [data, search]);

  const openAudit = (row) => {
    setAnswers({ ...(row.answers || {}) });
    const t = row.issue?.types || (row.issue?.type ? [row.issue.type] : []);
    setIssue({ types: t, severity: row.issue?.severity || '', description: row.issue?.description || '' });
    setRemarks(row.final_remarks || ''); setRating(row.overall_rating || '');
    setActualStart(row.actual_start_date || row.start_date || '');
    setActualEnd(row.actual_end_date || '');
    setModal({ open: true, row });
  };
  const setAns = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    const row = modal.row;
    setSaving(true);
    try {
      await api.post('/training/audit/final-submit', { batch_id: row.batch_id, answers, issue: answers.unresolved_issues === 'Yes' ? issue : {}, final_remarks: remarks, overall_rating: rating, actual_start_date: actualStart, actual_end_date: actualEnd });
      toast.success('Final audit saved');
      setModal({ open: false, row: null });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-[11px] text-slate-500">Trainer</Label>
            <Select value={trainerId} onValueChange={setTrainerId}><SelectTrigger className="h-9 mt-1 w-44" data-testid="final-trainer-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All trainers</SelectItem>{(data?.trainers || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
          {isAdmin && <div><Label className="text-[11px] text-slate-500">Manager</Label>
            <Select value={managerId} onValueChange={setManagerId}><SelectTrigger className="h-9 mt-1 w-44" data-testid="final-manager-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All managers</SelectItem>{(data?.managers || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}</SelectContent></Select></div>}
          <div><Label className="text-[11px] text-slate-500">Status</Label>
            <Select value={statusF} onValueChange={setStatusF}><SelectTrigger className="h-9 mt-1 w-40" data-testid="final-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="red">Pending</SelectItem><SelectItem value="green">Satisfactory</SelectItem><SelectItem value="amber">Issue Found</SelectItem></SelectContent></Select></div>
          <div><Label className="text-[11px] text-slate-500">Batch Type</Label>
            <Select value={batchType} onValueChange={setBatchType}><SelectTrigger className="h-9 mt-1 w-44" data-testid="final-batchtype-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{BATCH_TYPE_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>
          <div className="flex-1 min-w-[160px]"><Label className="text-[11px] text-slate-500">Search</Label>
            <div className="relative mt-1"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Batch no, name…" className="h-9 pl-8" data-testid="final-search" /></div></div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard testid="final-kpi-total" label="Completed Batches" value={data?.counts?.total || 0} />
        <KpiCard testid="final-kpi-done" label="Final Audit Done" value={data?.counts?.completed || 0} color="text-emerald-600" />
        <KpiCard testid="final-kpi-pending" label="Pending" value={data?.counts?.pending || 0} color="text-rose-600" />
        <KpiCard testid="final-kpi-completion" label="Completion" value={`${(data?.counts?.total || 0) ? Math.round((data.counts.completed / data.counts.total) * 100) : 0}%`} color="text-violet-600" />
      </div>

      {loading ? <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading…</div>
        : <GroupedBatchTable rows={rows} onAudit={openAudit} actionLabel="Final Audit" testid="final-audit-table" cols={[
            { label: 'Batch No', render: (r) => <span className="font-bold text-violet-600">#{r.batch_number}</span> },
            { label: 'Batch Name', render: (r) => r.batch_name },
            { label: 'Type', render: (r) => <Badge className={`text-[9px] border-0 ${r.is_weekend ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>{r.is_weekend ? 'Weekend' : 'Weekday'}</Badge> },
            { label: 'Planned End', render: (r) => <span className="text-slate-500">{fmtDate(r.planned_end_date || r.end_date)}</span> },
            { label: 'Actual End', render: (r) => r.actual_end_date ? <span className="text-slate-700 font-medium">{fmtDate(r.actual_end_date)}</span> : <span className="text-slate-300">—</span> },
            { label: 'Hours', render: (r) => (r.actual_hours != null ? <span className="font-semibold text-slate-700">{r.actual_hours}h</span> : '—') },
            { label: 'Rating', render: (r) => r.overall_rating ? <Badge className="text-[9px] bg-violet-100 text-violet-700 border-0">{r.overall_rating}</Badge> : '—' },
          ]} />}

      <Dialog open={modal.open} onOpenChange={(o) => !o && setModal({ open: false, row: null })}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="final-audit-modal">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle className="text-base flex items-center gap-2"><Award className="w-4 h-4 text-violet-600" />Final Audit — Batch {modal.row?.batch_number} · {modal.row?.trainer_name}</DialogTitle>
                <p className="text-[11px] text-slate-500">{modal.row?.batch_name} · {fmtDate(modal.row?.start_date)} → {fmtDate(modal.row?.end_date)}</p>
              </div>
              {modal.row && <BatchWhatsappButton link={modal.row.whatsapp_link} source={modal.row.whatsapp_source} className="shrink-0 mt-0.5" />}
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-1 space-y-4">
            {modal.row && <DetailToggle batchId={modal.row.batch_id} />}
            {/* Planned vs Actual dates + computed classes/hours */}
            {(() => {
              const isWknd = !!modal.row?.is_weekend;
              const exp = expectedSessions(actualStart, actualEnd, isWknd);
              return (
                <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3 space-y-3" data-testid="final-dates-block">
                  <div className="flex items-center justify-between">
                    <p className="text-[12px] font-bold tracking-wide uppercase text-violet-700">Batch Dates & Hours</p>
                    <Badge className="text-[10px] bg-white border border-violet-200 text-violet-700">{isWknd ? 'Weekend · 2 classes/wk · 2h each' : 'Weekday · 4 classes/wk · 1.5h each'}</Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[11px] text-slate-500">Original Planned End Date</Label>
                      <div className="h-9 mt-1 px-3 flex items-center rounded-md bg-slate-100 border border-slate-200 text-[13px] font-semibold text-slate-700" data-testid="final-planned-end">{fmtDate(modal.row?.planned_end_date || modal.row?.end_date) || '—'}</div>
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Actual Start Date</Label>
                      <Input type="date" value={actualStart} onChange={(e) => setActualStart(e.target.value)} className="h-9 mt-1" data-testid="final-actual-start" />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Actual End Date</Label>
                      <Input type="date" value={actualEnd} onChange={(e) => setActualEnd(e.target.value)} className="h-9 mt-1" data-testid="final-actual-end" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-md border border-slate-200 py-2 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">Actual Classes</p>
                      <p className="text-lg font-bold text-slate-800" data-testid="final-actual-classes">{actualStart && actualEnd ? exp.classes : '—'}</p>
                    </div>
                    <div className="bg-white rounded-md border border-slate-200 py-2 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">Actual Training Hours</p>
                      <p className="text-lg font-bold text-slate-800" data-testid="final-actual-hours">{actualStart && actualEnd ? `${exp.hours} h` : '—'}</p>
                    </div>
                  </div>
                  {actualEnd && modal.row?.planned_end_date && actualEnd !== modal.row.planned_end_date && (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1">
                      Actual end differs from planned end ({fmtDate(modal.row.planned_end_date)}).
                    </p>
                  )}
                </div>
              );
            })()}
            <div>{(final.questions || []).map((q) => <RadioRow key={q.key} q={q} value={answers[q.key]} onChange={setAns} />)}</div>
            {answers.unresolved_issues === 'Yes' && <IssueBlock schema={{ issue_types: final.issue_types, issue_severities: final.issue_severities }} issue={issue} setIssue={setIssue} testidPrefix="final" />}
            <div>
              <Label className="text-[11px]">Overall Rating</Label>
              <Select value={rating} onValueChange={setRating}><SelectTrigger className="h-9 mt-1 w-48" data-testid="final-rating"><SelectValue placeholder="Select rating…" /></SelectTrigger>
                <SelectContent>{(final.ratings || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-[11px]">Final Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className="mt-1 text-[13px]" data-testid="final-remarks" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal({ open: false, row: null })} className="h-9">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="h-9 bg-violet-600 hover:bg-violet-700" data-testid="final-submit-btn">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Submit Final Audit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
